import { useFragment, useMutation } from '@apollo/client/react'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { graphql } from '@/gql'
import { Text } from '@/components/base/text'
import { Badge } from '../base/badge'
import { RoleBadge } from '@/components/connector_family_pins/role_badge'
import { Select } from '../base/select'
import type { ConnectorFamilyPinRole } from '@/gql/graphql'
import { Input, InputGroup } from '../base/input'
import { useState, type ReactNode } from 'react'
import { Field, Label } from '../base/fieldset'
import { PulseLoader } from 'react-spinners'
import { steppableColors } from '@/constants'

export const ItemFragment = graphql(`
  fragment ItemDetail on ItemType {
    id
    name
    panelConnectors { id label connector { id connectorFamily { id connectorFamilyPins { id } } } }
    itemType {
      id
      name
    }
    itemTestSteps {
      id
      testStep {
        id
        name
        steppable {
          __typename
          ... on NoopTestStepType { id result }
          ... on HipotTestStepType { id voltage connectorFamilyPin { id } }
          ... on ResistanceTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on ResistanceFourWireTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on CapacitanceTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on DiodeTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on TwistedPairTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on RcdTestStepType { id breakerCount phaseAngle tripCurrent }
          ... on LoadTestStepType { id loadCurrent }
          ... on WireTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on WireFourWireTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
          ... on LinkTestStepType { id connectorFamilyPinA { id } connectorFamilyPinB { id } }
        }
      }
      itemTestStepParameters { id key value }
      itemTestStepMappings {
        id
        panelConnector { id label }
        connectorFamilyPin { id name role connectorFamily { name } }
        source
      }
    }
  }
`)

const UpdateMapping = graphql(`
  mutation UpdateItemTestStepMapping($input: UpdateItemTestStepMappingInput!) {
    updateItemTestStepMapping(input: $input) {
      errors { path message }
      itemTestStepMapping { id panelConnector { id label } connectorFamilyPin { id } }
    }
  }
`)

const UpdateParameter = graphql(`
  mutation UpdateItemTestStepParameter($input: UpdateItemTestStepParameterInput!) {
    updateItemTestStepParameter(input: $input) {
      errors { path message }
      itemTestStepParameter { id key value }
    }
  }
`)

interface Props {
  id: string
}

const stepParameters: { [steppable: string]: { [key: string]: { name: string, placeholder: string, leftContent?: ReactNode, rightContent?: ReactNode } } } = {
  WireTestStepType: {
    resistance: {
      name: 'Resistance',
      placeholder: '700',
      rightContent: <span className="text-xs" data-slot="icon">mΩ</span>,
    },
  },
  WireFourWireTestStepType: {
    minimum_resistance: {
      name: 'Minimum resistance',
      placeholder: '250',
      rightContent: <span className="text-xs" data-slot="icon">mΩ</span>,
    },
    maximum_resistance: {
      name: 'Maximum resistance',
      placeholder: '400',
      rightContent: <span className="text-xs" data-slot="icon">mΩ</span>,
    }
  },
  CapacitanceTestStepType: {},
  DiodeTestStepType: {},
  HipotTestStepType: {},
  LinkTestStepType: {},
  LoadTestStepType: {},
  NoopTestStepType: {},
  RcdTestStepType: {},
  ResistanceTestStepType: {
    resistance: {
      name: 'Resistance',
      placeholder: '300',
      rightContent: <span className="text-xs" data-slot="icon">mΩ</span>,
    },
    tolerance: {
      name: 'Tolerance',
      placeholder: '5',
      rightContent: <span className="text-xs" data-slot="icon">%</span>,
    }
  },
  ResistanceFourWireTestStepType: {
    resistance: {
      name: 'Resistance',
      placeholder: '300',
      rightContent: <span className="text-xs" data-slot="icon">mΩ</span>,
    },
    tolerance: {
      name: 'Tolerance',
      placeholder: '5',
      rightContent: <span className="text-xs" data-slot="icon">%</span>,
    }
  },
  TwistedPairTestStepType: {},
}

export const Item = ({ id }: Props) => {
  const { data, complete } = useFragment({
    fragment: ItemFragment,
    from: { __typename: 'ItemType', id },
  })

  const [updateMapping] = useMutation(UpdateMapping)
  const [updateParameter] = useMutation(UpdateParameter)

  const [upsertLoading, setUpsertLoading] = useState<{ [id: string]: boolean }>({})
  // const [_applyAllConnectors, setApplyAllConnectors] = useState<string[]>(['unattached', 'unattached'])

  if (!complete) return <LoadingSkeleton table />

  const buildPanelConnectorSelector = (mapping?: { id: string, panelConnector: { id: string } | null, connectorFamilyPin: { id: string, name: string, role: ConnectorFamilyPinRole, connectorFamily: { name: string } }, source: string }) => {
    if (!mapping) return <></>

    const value = mapping.panelConnector?.id || 'unattached'

    return (
      <Field>
        <Label>{ mapping.connectorFamilyPin.connectorFamily.name } - { mapping.connectorFamilyPin.name } <RoleBadge role={ mapping.connectorFamilyPin.role } />{ upsertLoading[mapping.id] === true && <PulseLoader color="lightgray" size={ 12 } /> }</Label>
        <Select disabled={ upsertLoading[mapping.id] === true } value={ value } onChange={ e => upsertMapping(mapping.id, e.currentTarget.value) }>
          <option value="unattached" disabled>Not attached yet</option>
          { data.panelConnectors.filter(pc => pc.connector.connectorFamily.connectorFamilyPins.map(p => p.id).includes(mapping.connectorFamilyPin.id)).map(connector => <option value={ connector.id }>{ connector.label }</option>) }
        </Select>
      </Field>
    )
  }

  // const applyConnectorToAll = (index: number, connector: string) => {
  //   setApplyAllConnectors(acc => {
  //     acc[index] = connector
  //     return acc
  //   })
  // }

  const upsertMapping = (itemTestStepMappingId: string, panelConnectorId: string) => {
    setUpsertLoading(l => ({ ...l, [itemTestStepMappingId]: true }))

    updateMapping({ variables: { input: { id: itemTestStepMappingId, panelConnectorId } } })
      .finally(() => setUpsertLoading(l => ({ ...l, [itemTestStepMappingId]: false })))
  }

  const upsertParameter = (itemTestStepParameterId: string, value: string) => {
    setUpsertLoading(l => ({ ...l, [itemTestStepParameterId]: true }))

    updateParameter({ variables: { input: { id: itemTestStepParameterId, value } } })
      .finally(() => setUpsertLoading(l => ({ ...l, [itemTestStepParameterId]: false })))
  }

  return (
    <>
      { data && <div className="flex flex-col grow">
        <span className="flex flex-row gap-3 items-center">
          <Text>Item type: </Text>
          <Badge color="green">{ data.itemType.name }</Badge>
        </span>
        <Text>{ data.name }</Text>
        <div className="flex flex-col divide-y divide-zinc-500">
          { /* { data.panelConnectors.length > 0 && <div className="grid grid-cols-5 gap-3 items-center py-6">
            <span />
            <Text>Attach this connector to all steps</Text>
            <Select value={ applyAllConnectors[0] } onChange={ e => applyConnectorToAll(0, e.target.value) }>
              <option value="unattached" disabled>Not attached yet</option>
              { data.panelConnectors.map(connector => <option value={ connector.id }>{ connector.label }</option>) }
            </Select>
          </div> } */ }
          { data.itemTestSteps.map(its => (
            <div className="grid grid-cols-5 gap-3 items-center py-6">
              <Text>{ its.testStep.name }</Text>
              <span><Badge color={ steppableColors[its.testStep.steppable.__typename] }>{ its.testStep.steppable.__typename.replace('TestStepType', '') }</Badge></span>
              {
                its.itemTestStepMappings.toSorted((a, b) => a.source < b.source ? -1 : 1).map(mapping => buildPanelConnectorSelector(mapping))
              }
              <div className="flex flex-col gap-3">
                {
                  its.itemTestStepParameters.map(parameter => (
                    <Field>
                      <Label>{ stepParameters[its.testStep.steppable.__typename][parameter.key].name }</Label>
                      <InputGroup>
                        { stepParameters[its.testStep.steppable.__typename][parameter.key].leftContent }
                        <Input defaultValue={ parameter.value || '' } placeholder={ stepParameters[its.testStep.steppable.__typename][parameter.key].placeholder } onBlur={ e => upsertParameter(parameter.id, e.currentTarget.value) } />
                        { stepParameters[its.testStep.steppable.__typename][parameter.key].rightContent }
                      </InputGroup>
                    </Field>
                  ))
                }
              </div>
            </div>
          )) }
        </div>
      </div> }
    </>
  )
}
